import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  BatchSpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { W3CTextMapPropagator } from '../w3ctextMapPropagator';
import { SpanStatusCode, trace, context } from '@opentelemetry/api';
import { ReactNode } from 'react';
import { getResource } from './resource';
const { ZoneContextManager } = require('@opentelemetry/context-zone');


export const userAttributes: Record<string, any> = {};

export const setOtelLogsUserAttributes = (newAttributes: Record<string, any>) => {
  Object.assign(userAttributes, newAttributes);
};

function randomHex(len: number): string {
  return Array.from({ length: len }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join('');
}

let sessionTraceId = '';
let sessionSpanId = '';
function randomIds(): { traceId: string; spanId: string } {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return {
      traceId: crypto.randomUUID().replace(/-/g, ''),
      spanId: crypto.randomUUID().replace(/-/g, '').slice(0, 16),
    };
  }
  return { traceId: randomHex(32), spanId: randomHex(16) };
}

// Anchor every session-level log (console errors, window errors, and HTTP-error
// logs that fire outside an active span) to a single REAL span exported to Tempo,
// so the traceID those logs carry actually resolves there instead of 404ing.
// Mirrors the mobile-charts approach (which anchors to its chart.init span).
function ensureSessionIds(): { traceId: string; spanId: string } {
  if (sessionTraceId) return { traceId: sessionTraceId, spanId: sessionSpanId };
  try {
    const span = trace.getTracer('session').startSpan('session.init');
    span.setAttributes(userAttributes);
    const ctx = span.spanContext();
    span.end(); // end immediately so the batch exporter ships it right away
    if (ctx && ctx.traceId && ctx.traceId !== '00000000000000000000000000000000') {
      sessionTraceId = ctx.traceId;
      sessionSpanId = ctx.spanId;
      return { traceId: sessionTraceId, spanId: sessionSpanId };
    }
  } catch {
    // tracing not ready yet / failed — fall through to a synthetic id
  }
  // Not cached: a later call can still upgrade to a resolvable span id once the
  // tracer provider is registered.
  return randomIds();
}

export function getTraceContext(): { traceID: string; spanID: string } {
  const spanContext = trace.getSpan(context.active())?.spanContext();
  if (spanContext) {
    return { traceID: spanContext.traceId, spanID: spanContext.spanId };
  }
  const session = ensureSessionIds();
  return { traceID: session.traceId, spanID: session.spanId };
}

let provider: WebTracerProvider;

const fetchInstrumentation = new FetchInstrumentation({
  // Ignore non-rupeezy.in hosts and the high-frequency stockdata feed (matches
  // the Web App config) so market-data polling doesn't flood Tempo with spans.
  ignoreUrls: [/^(?!.*rupeezy\.in).*/, /https:\/\/stockdata\.rupeezy\.in/],
  clearTimingResources: true,
  propagateTraceHeaderCorsUrls: [/^https?:\/\/(?!(sst)\.)[\w-]+\.rupeezy\.in(\/|$)/,],
  applyCustomAttributesOnSpan: ((span, request, fetchError) => {
    span = span.setAttributes(userAttributes)
    if (fetchError?.status === undefined || fetchError.status > 400) {
      span = span.setStatus({ code: SpanStatusCode.ERROR })
    }
  }),

});

const xmlInstrumentation = new XMLHttpRequestInstrumentation({
  ignoreUrls: [/^(?!.*rupeezy\.in).*/, /https:\/\/stockdata\.rupeezy\.in/],
  clearTimingResources: true,
  propagateTraceHeaderCorsUrls: [/^https?:\/\/(?:[\w-]+\.)?rupeezy\.in(\/|$)/],
  applyCustomAttributesOnSpan: ((span, request) => {
    span = span.setAttributes(userAttributes)
    if (request?.status === undefined || request.status > 400) {
      span = span.setStatus({ code: SpanStatusCode.ERROR })
    }
  }),
});

function registerProvider(provider: WebTracerProvider) {
  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTextMapPropagator(),
  });
  return provider;
}

function registerInstrumentation(provider: WebTracerProvider) {
  registerInstrumentations({
    instrumentations: [
      fetchInstrumentation,
      xmlInstrumentation
    ],
    tracerProvider: provider,
  });
}
export function forceFlushTraces(): Promise<void> {
  if (provider) return provider.forceFlush();
  return Promise.resolve();
}

export function setupTracing() {
  if (provider) return provider;
  provider = new WebTracerProvider({
    resource: getResource(),
    spanProcessors: [new BatchSpanProcessor(new OTLPTraceExporter(
      {
        url: 'https://tracer.rupeezy.in/v1/traces',
      }
    ), { maxExportBatchSize: 50 })],
  });

  provider = registerProvider(provider);
  registerInstrumentation(provider);
  return provider;
}
interface TraceProviderProps {
  children: ReactNode;
}
export default function TraceProvider({ children }: TraceProviderProps) {

  return (
    <>
      {children}
    </>
  );

}